import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type UsePracticeMoreMenuParams = {
  onChooseWhatToPractice: () => void;
  onGenerateAutomatically: () => void;
};

type UsePracticeMoreMenuResult = {
  containerRef: React.RefObject<HTMLDivElement>;
  handleChooseWhatToPractice: () => void;
  handleGenerateAutomatically: () => void;
  isOpen: boolean;
  t: (key: string) => string;
  toggleMenu: () => void;
};

/**
 * State + handlers for the "Practicas más" dropdown menu (TANAI-1869).
 */
function usePracticeMoreMenu({ onChooseWhatToPractice, onGenerateAutomatically }: UsePracticeMoreMenuParams): UsePracticeMoreMenuResult {
  const { t } = useTranslation(['chatbot']);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeMenu();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeMenu, isOpen]);

  const handleGenerateAutomatically = useCallback(() => {
    closeMenu();
    onGenerateAutomatically();
  }, [closeMenu, onGenerateAutomatically]);

  const handleChooseWhatToPractice = useCallback(() => {
    closeMenu();
    onChooseWhatToPractice();
  }, [closeMenu, onChooseWhatToPractice]);

  return {
    containerRef,
    handleChooseWhatToPractice,
    handleGenerateAutomatically,
    isOpen,
    t,
    toggleMenu,
  };
}

export default usePracticeMoreMenu;
